/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {sanitizeAccount} from '@ion/core/src/entities/Accounts';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {AccountLink, SubAccountLink} from '@ion/core/src/types/entities';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import IconRadioLabel from '../radio/IconRadioLabel';
import RadioButton from '../radio/RadioButton';

export interface GroupChooserRadioListProps {
  groups: AccountLink[] | SubAccountLink[];
  handleNextClick: Function;
};

export const GROUP_CHOOSER_ITEM_PREFIX = 'group-chooser-item-';

const useStyles = makeStyles(theme => ({
  slideItem : {
    maxWidth                      : '95%',
    '& .MuiFormControlLabel-root' : {
      width       : '100%',
      marginLeft  : '0px',
      marginRight : '0px'
    },
    '& .MuiFormControlLabel-label' : {
      width : '100%'
    }
  },
  currentSlide : {
    maxWidth                      : '96%',
    '& .MuiFormControlLabel-root' : {
      width                 : '96%',
      bottom                : '14px',
      height                : '44px',
      position              : 'relative',
      marginLeft            : '22px',
      marginRight           : '2px',
      borderRadius          : '8px',
      '&:has(.Mui-checked)' : {
        backgroundColor : dlsColor('paleBlue')
      },
      '& .MuiTypography-body1' : {
        width : '100%'
      }
    },
    '& .MuiFormControlLabel-label' : {
      width : '96%'
    }
  }
}));

/**
 * @GroupChooserRadioList
 *
 * Radio Item that intentionally stops click propagation so that it can be
 * nested inside another clickable component like an accordion. Abstracted
 * mainly to prevent code duplication.
 *
 * ```typescript
 * import {GroupChooserRadioList} from '@ion/dls/src/components/groupchooser';
 *
 * <GroupChooserRadioList groups={usersAccounts}
 *   handleNextClick={onIconClick}  />
 *
 * ```
 * @param groups list of groups to go in this list
 * @param handleNextClick callback when the radio label icon is clicked
 */
const GroupChooserRadioList = ({
  groups,
  handleNextClick
}: GroupChooserRadioListProps) => {
  const classes = useStyles();

  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  return (
    <>
      {
        groups.map((group, i) => {
          const account  = sanitizeAccount(group);
          const keyId    = entityId(account) || i;
          const uniqueId = `${GROUP_CHOOSER_ITEM_PREFIX}${keyId}`;
          return (
            <div className={isCurrentTheme ? classes.currentSlide : classes.slideItem} key={uniqueId} id={uniqueId}>
              <RadioButton value={account.href}
                label={<IconRadioLabel label={EntityLabel.get(account)}
                  onIconClick={account.subAccounts ? function(e) {
                    e.stopPropagation();
                    e.preventDefault();
                    handleNextClick(group);
                  } : undefined} />} />
            </div>
          );
        })
      }
    </>
  );
};

export default GroupChooserRadioList;
